import { Col, Form, Input, Row, Select } from "antd";
import React, { useEffect, useState } from "react";
import { DraggableModal } from "../../../../components/DraggableModal";
import SelectTerritorialUnitsCode from "../../../../components/selects/SelectTerritorialUnitsCode";
import GeneralSelect from "../../../../components/selects/general/GeneralSelect";
import { EntBasicHB } from "../../../../models/handBooks/basicHB";
import { EntInitialFilter } from "../../../../models/filters/initialFilter";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { EntAnimalRegister } from "../../../../models/organic/animalRegister";
import { SelectAnimalType } from "../../../../components/selects";
import { useLazyGetFarmCategoryListQuery } from "../../../../redux/api/handBooksApi/organic/farmCategoryApi";

enum FormField {
  id,
  year,
  regionId,
  areaId,
  territorialUnitId,
  farmCategoryId,
  animalTypeId,
  headAmount,
  dataSource
}


interface IProps {
  show: boolean;
  item: EntAnimalRegister;
  onHide: () => void;
  onSubmit: (pos: EntAnimalRegister) => Promise<boolean>;
}

export const AddAnimalRegisterModal: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const [selectedRegionPolygonJson, setSelectedRegionPolygonJson] = useState<string|null>(null);

  const [selectedRegionId, setSelectedRegionId] = useState<number|null>(null);
  const [selectedRegionCode, setSelectedRegionCode] = useState<string|null>(null);
  const [selectedAreaCode, setSelectedAreaCode] = useState<string|null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number|null>(null);

  useEffect(() => {
	if (props.show) setFieldsFromEntity();
	setSelectedRegionId(props.item.region?.id || null);
	setSelectedAreaId(props.item.area?.id || null);
	setSelectedAreaCode(props.item.territorialUnit?.code || null);
  }, [props.show]);

  const setFieldsFromEntity = (): void => {
	form.setFields([
	  { name: FormField.year, value: props.item.year },
	  { name: FormField.regionId, value: props.item.region?.id },
	  { name: FormField.areaId, value: props.item.area?.id },
	  { name: FormField.territorialUnitId, value: props.item.territorialUnit?.id },
	  { name: FormField.farmCategoryId, value: props.item.farmCategory?.id },
	  { name: FormField.animalTypeId, value: props.item.animalType?.id },
	  { name: FormField.headAmount, value: props.item.headAmount },
	  { name: FormField.dataSource, value: props.item.dataSource },
	]);
  };

  const setEntityFromFields = (): EntAnimalRegister => {
	let res = _.cloneDeep(props.item);
	res.year = form.getFieldValue(FormField.year);
	res.regionId = form.getFieldValue(FormField.regionId);
	res.areaId = form.getFieldValue(FormField.areaId);
	res.territorialUnitId = form.getFieldValue(FormField.territorialUnitId);
	res.farmCategoryId = form.getFieldValue(FormField.farmCategoryId);
	res.animalTypeId = form.getFieldValue(FormField.animalTypeId);
	res.headAmount = form.getFieldValue(FormField.headAmount);
	res.dataSource = form.getFieldValue(FormField.dataSource);
	return res;
  }

  const handleSubmit = async () => {
	setLoading(true);
	try {
	  await form.validateFields();
	  const data = setEntityFromFields();
	  const result = await props.onSubmit(data);
	  if (result) props.onHide()
	} finally {
	  setLoading(false);
	}
  }

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, index) => currentYear - index);

  return (
	<>
	  <DraggableModal
		title={ props.item.id === null ? t('general.add') : t('general.edit') }
		visible={ props.show }
		onCancel={ props.onHide }
		onSubmit={ handleSubmit }
		submitLoading={ loading }
		width={ 800 }
	  >
		<Form
		  initialValues={ { remember: true } }
		  autoComplete="off"
		  layout="vertical"
		  form={ form }
		>
		  <Row style={ { margin: 0 } } gutter={ 16 }>
			<Col className="gutter-row" span={ 12 }>
			  <Form.Item
				label={ t('year') }
				name={ FormField.year }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Select
				  placeholder={ t("msg.selectPlaceholder") }
				  allowClear
				>
				  { years.map((year) => (
					<Select.Option key={ year } value={ year }>
					  { year }
					</Select.Option>
				  )) }
				</Select>
			  </Form.Item>
			  <Form.Item
				label={ t('titles.territorial_unit_area') }
				name={ FormField.regionId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTerritorialUnitsCode
				  parentId={ 1 }
				  onChange={ (value, option: any) => {
					setSelectedRegionId(Number(value));
					setSelectedRegionPolygonJson(option?.data?.polygonJson);
					setSelectedRegionCode(option?.data?.code);
					setSelectedAreaCode(null);
					setSelectedAreaId(null);
					form.setFieldsValue({
					  [FormField.regionId]: value,
					  [FormField.areaId]: null, // Сбрасываем район
					  [FormField.territorialUnitId]: null, // Сбрасываем населенный пункт
					});
				  } }
				/>
			  </Form.Item>
			  <Form.Item
				label={ t('Район') }
				name={ FormField.areaId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTerritorialUnitsCode
				  type={ ['AREA'] }
				  parentId={ selectedRegionId }
				  onChange={ (value, option: any) => {
					setSelectedRegionPolygonJson(option?.data?.polygonJson);
					setSelectedAreaCode(option?.data?.code || null);
					setSelectedAreaId(value as number);
					form.setFields([{ name: FormField.territorialUnitId, value: null }]);
				  } }
				/>
			  </Form.Item>
			  <Form.Item
				label={ t("Населенный пункт") }
				name={ FormField.territorialUnitId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectTerritorialUnitsCode areaCode={ selectedAreaCode }/>
			  </Form.Item>
			</Col>
			<Col className="gutter-row" span={ 12 }>
			  <Form.Item
				label={ t('Категория ферм') }
				name={ FormField.farmCategoryId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<GeneralSelect<EntBasicHB, EntInitialFilter>
				  queryHook={ useLazyGetFarmCategoryListQuery }
				  initialFilter={ new EntInitialFilter() }
				/>
			  </Form.Item>
			  <Form.Item
				label={ t('Вид животного') }
				name={ FormField.animalTypeId }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<SelectAnimalType/>
			  </Form.Item>
			  <Form.Item
				label={ t('Количество голов') }
				name={ FormField.headAmount }
				rules={ [{ required: true, message: t("msg.fill_in_the_field") }] }
			  >
				<Input type="number"/>
			  </Form.Item>
			  <Form.Item
				label={ t('Источник финансирования') }
				name={ FormField.dataSource }
			  >
				<Input type="text"/>
			  </Form.Item>
			</Col>
		  </Row>
		</Form>
	  </DraggableModal>
	</>
  )
}
